import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../supabaseClient";
import { PAYMENTS_ENABLED } from "../config";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  IconButton,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  InputAdornment,
  ListItemText,
  FormHelperText,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import {
  normalizeCity,
  divisionLabelForProduct,
  productTypeOf,
  uniqueDivisionLabelsForCity,
} from "../utils/productLabels";
import { logRegistrationEvent } from "../utils/funnelAnalytics";
import { isValidEmail } from "../utils/isValidEmail";
import Checkbox from "./ui/Checkbox";
import Markdown from "./Markdown";
import { toProductMarkdown } from "../utils/productDescription";

const REG_FONT = `'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;

const RegSectionLabel = ({ children, sx, ...props }) => (
  <Typography
    variant="overline"
    sx={{
      display: "block",
      fontFamily: REG_FONT,
      fontSize: "0.6875rem",
      fontWeight: 600,
      letterSpacing: "0.08em",
      color: "rgba(100, 116, 139, 0.95)",
      mb: 0.75,
      lineHeight: 1.4,
      ...sx,
    }}
    {...props}
  >
    {children}
  </Typography>
);

const RegTitle = ({ children, sx, ...props }) => (
  <Typography
    component="h2"
    sx={{
      fontFamily: REG_FONT,
      fontSize: { xs: "1.125rem", sm: "1.25rem" },
      fontWeight: 600,
      letterSpacing: "-0.025em",
      color: "#0f172a",
      lineHeight: 1.3,
      ...sx,
    }}
    {...props}
  >
    {children}
  </Typography>
);

const RegBody = ({ children, sx, ...props }) => (
  <Typography
    sx={{
      fontFamily: REG_FONT,
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: 1.65,
      color: "#475569",
      ...sx,
    }}
    {...props}
  >
    {children}
  </Typography>
);

const RegMeta = ({ label, children }) => (
  <Box sx={{ mb: 1.75 }}>
    <Typography
      sx={{
        fontFamily: REG_FONT,
        fontSize: "0.6875rem",
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "#64748b",
        mb: 0.35,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: REG_FONT,
        fontSize: "0.9375rem",
        fontWeight: 500,
        color: "#0f172a",
        lineHeight: 1.5,
      }}
    >
      {children}
    </Typography>
  </Box>
);

const REGIONS = [
  { id: "Mississauga", label: "Mississauga", divisions: [{ id: "League", label: "League" }] },
  {
    id: "Cambridge",
    label: "Cambridge",
    divisions: [
      { id: "DIV 1", label: "DIV 1" },
      { id: "DIV 2", label: "DIV 2" },
      { id: "Tournament", label: "Tournament" },
    ],
  },
];

const EMAIL_CONSENT_FINE_PRINT =
  "By checking this box, you consent to Hadaf Soccer League collecting and storing the email address on your account for registration records, payment confirmation, and league-related contact about this team. You may contact us to update or remove this contact information.";

const REGISTRATION_OPTIONS = [
  {
    value: "individual",
    label: "Individual player",
    description: "Register yourself only. You’ll be placed on a team after checkout.",
  },
  {
    value: "team",
    label: "Full team (captain)",
    description: "Register your squad: you pay once for the team and add every player’s details.",
  },
];

const normalizeNumber = (v) => {
  if (v == null) return null;
  const cleaned = String(v).replace(/[^0-9.]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

const formatCost = (cost) => {
  const n = normalizeNumber(cost);
  if (n == null) return "—";
  return `$${n}`;
};

const paidCountKeyForProduct = (prod) => {
  if (prod?.id != null && String(prod.id).trim()) return `product__${String(prod.id)}`;
  const c = prod?.city || "";
  return `${c}__${divisionLabelForProduct(c, prod?.division, prod)}`;
};

/** Shareable URL for a specific product (region + division + productId). */
const buildRegistrationProductUrl = (origin, region, division, productId, sport) => {
  const q = new URLSearchParams();
  q.set("region", region);
  q.set("division", division);
  q.set("productId", String(productId));
  if (sport) q.set("sport", sport);
  const base = origin || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}/register/apply?${q.toString()}`;
};

const JERSEY_SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

const normalizeIndividualSignupQuestions = (raw) => {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.map((q) => String(q).trim()).filter(Boolean);
  if (typeof raw === "string") {
    try {
      return normalizeIndividualSignupQuestions(JSON.parse(raw));
    } catch {
      return [];
    }
  }
  return [];
};

const individualSignupFieldKind = (question) => {
  const q = String(question || "").toLowerCase();
  if (q.includes("jersey size")) return "jersey_size";
  if (q.includes("d.o.b") || q.includes("date of birth")) return "dob";
  if (q.includes("volunteer")) return "volunteer";
  if (q.includes("email")) return "email";
  if (q.includes("allerg") || q.includes("medical")) return "multiline";
  return "text";
};

const signupAnswerGuardianName = (answers, questions) => {
  const q = questions.find((x) => /parent\/guardian|guardian full name/i.test(String(x)));
  return q ? String(answers[q] ?? "").trim() : "";
};

const signupAnswerGuardianPhone = (answers, questions) => {
  const q = questions.find((x) => /guardian phone/i.test(String(x)));
  return q ? String(answers[q] ?? "").trim() : "";
};

const isValidIsoDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v ?? "").trim());

const validateIndividualSignupAnswers = (questions, answers) => {
  const e = {};
  questions.forEach((q, i) => {
    const key = `individual_q_${i}`;
    const kind = individualSignupFieldKind(q);
    const v = String(answers[q] ?? "").trim();
    if (kind === "volunteer") {
      if (!/^(yes|no)$/i.test(v)) e[key] = "Please select Yes or No";
    } else if (kind === "email") {
      if (!v) e[key] = "Required";
      else if (!isValidEmail(v)) e[key] = "Enter a valid email address";
    } else if (kind === "jersey_size") {
      if (!v) e[key] = "Required";
      else if (!JERSEY_SIZES.includes(v)) e[key] = "Select a jersey size";
    } else if (kind === "dob") {
      if (!v) e[key] = "Required";
      else if (!isValidIsoDate(v)) e[key] = "Enter a valid date";
    } else if (!v) {
      e[key] = "Required";
    }
  });
  return e;
};

const initialPlayer = () => ({ name: "", email: "", jersey_number: "", jersey_size: "", instagram: "" });

const APPLY_STEP_NAMES = ["select_region", "select_division", "registration_type", "details_checkout"];

const RegisterFlow = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [region, setRegion] = useState("");
  const [division, setDivision] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [registrationType, setRegistrationType] = useState("individual");
  const [teamName, setTeamName] = useState("");
  const [players, setPlayers] = useState([initialPlayer()]);
  const [captainName, setCaptainName] = useState("");
  const [captainPhone, setCaptainPhone] = useState("");
  const [emailContactConsent, setEmailContactConsent] = useState(false);
  const [products, setProducts] = useState([]);
  const [paidCountsByKey, setPaidCountsByKey] = useState({});
  const [productId, setProductId] = useState(null);
  /** Sport filter on /apply when region+division are set but product is not (wizard-only). */
  const [wizardBrowseSport, setWizardBrowseSport] = useState("");
  const [errors, setErrors] = useState({});
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [individualSignupAnswers, setIndividualSignupAnswers] = useState({});
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));

  // Prefill from SelectDivision OR query params (for returning after sign-in)
  useEffect(() => {
    const s = location.state;
    const qRegion = searchParams.get("region");
    const qDivision = searchParams.get("division");
    const qStep = searchParams.get("step");
    const qRegistrationType = searchParams.get("registrationType");
    const qProductId = searchParams.get("productId");
    const draftKey = searchParams.get("draftKey");

    if (draftKey) {
      try {
        const raw =
          localStorage.getItem(draftKey) ??
          sessionStorage.getItem(draftKey);
        if (raw) {
          const d = JSON.parse(raw);
          if (d.region) setRegion(d.region);
          if (d.division) setDivision(d.division);
          if (d.registrationType) setRegistrationType(d.registrationType);
          if (typeof d.step === "number") setStep(d.step);
          if (d.teamName != null) setTeamName(d.teamName);
          if (d.captainName != null) setCaptainName(d.captainName);
          if (d.captainPhone != null) setCaptainPhone(d.captainPhone);
          if (d.emailContactConsent != null) setEmailContactConsent(Boolean(d.emailContactConsent));
          if (Array.isArray(d.players) && d.players.length) setPlayers(d.players);
          if (d.individualSignupAnswers != null && typeof d.individualSignupAnswers === "object") {
            setIndividualSignupAnswers(d.individualSignupAnswers);
          }
          if (d.wizardBrowseSport != null) setWizardBrowseSport(String(d.wizardBrowseSport));
          if (d.productId != null) setProductId(d.productId);
          try {
            localStorage.removeItem(draftKey);
          } catch (_) {
            // ignore
          }
          try {
            sessionStorage.removeItem(draftKey);
          } catch (_) {
            // ignore
          }
          return;
        }
      } catch (e) {
        // ignore corrupted draft
      }
    }

    const nextRegion = s?.region ?? qRegion ?? "";
    const nextDivision = s?.division ?? qDivision ?? "";
    const nextProductId = s?.productId ?? qProductId ?? null;

    if (nextRegion) setRegion(nextRegion);
    if (nextDivision) setDivision(nextDivision);
    if (nextProductId) setProductId(nextProductId);
    else {
      const qb = searchParams.get("browseSport");
      setWizardBrowseSport(qb || "");
    }

    const parsedStep = qStep ? Number(qStep) : null;
    if (parsedStep != null && Number.isFinite(parsedStep)) {
      setStep(parsedStep);
    } else if (nextRegion && nextDivision) {
      setStep(2);
    }

    if (qRegistrationType === "team" || qRegistrationType === "individual") {
      setRegistrationType(qRegistrationType);
    }
  }, [location.state, searchParams]);

  // Fetch products for cost, timing, capacity, location
  useEffect(() => {
    const fetchProducts = async () => {
      const { data } = await supabase
        .from("products")
        .select(
          "id, city, division, capacity, cost, location, timing, description, name, image, sport, age_group, team_registeration, individual_registeration, individual_signup_questions, product_type, captain_only_registration, require_email_consent, sold_out"
        );
      setProducts(data || []);
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchPaidCounts = async () => {
      const { data, error } = await supabase
        .from("league_teams")
        .select("region, division, product_id")
        .eq("payment_status", "paid");
      if (error) {
        setPaidCountsByKey({});
        return;
      }
      const counts = {};
      for (const row of data || []) {
        if (row?.product_id != null && String(row.product_id).trim()) {
          const key = `product__${String(row.product_id)}`;
          counts[key] = (counts[key] || 0) + 1;
          continue;
        }
        const region = row?.region || "";
        const divisionLabel = divisionLabelForProduct(region, row?.division);
        const key = `${region}__${divisionLabel}`;
        counts[key] = (counts[key] || 0) + 1;
      }
      setPaidCountsByKey(counts);
    };
    fetchPaidCounts();
  }, []);

  useEffect(() => {
    if (user?.user_metadata?.full_name && !captainName) setCaptainName(user.user_metadata.full_name);
  }, [user, captainName]);

  // Deep link: ?productId= only — fill region/division after products load
  useEffect(() => {
    if (!products.length) return;
    const qProductId = searchParams.get("productId");
    if (!qProductId) return;
    const hasRegion = Boolean(searchParams.get("region") || location.state?.region);
    const hasDivision = Boolean(searchParams.get("division") || location.state?.division);
    if (hasRegion && hasDivision) return;
    const p = products.find((x) => String(x.id) === String(qProductId));
    if (!p) return;
    const divLabel = divisionLabelForProduct(p.city, p.division, p);
    setRegion(p.city);
    setDivision(divLabel);
    setProductId(String(p.id));
    setStep(2);
  }, [products, searchParams, location.state]);

  const cityProducts = useMemo(() => {
    if (!region) return [];
    return products.filter((p) => normalizeCity(p.city) === normalizeCity(region));
  }, [products, region]);

  /** Divisions this region actually has programs for (e.g. 35+), not a fixed list. */
  const divisions = useMemo(() => {
    const fromProducts = uniqueDivisionLabelsForCity(region, cityProducts);
    if (fromProducts.length) return fromProducts.map((d) => ({ id: d, label: d }));
    return REGIONS.find((r) => r.id === region)?.divisions ?? [];
  }, [region, cityProducts]);

  const currentProduct = useMemo(() => {
    if (productId) {
      return products.find((p) => String(p.id) === String(productId)) ?? null;
    }
    if (!region || !division) return null;
    const list = products.filter(
      (p) =>
        normalizeCity(p.city) === normalizeCity(region) &&
        divisionLabelForProduct(p.city, p.division, p) === division
    );
    if (list.length === 1) return list[0];
    return null;
  }, [products, productId, region, division]);

  const individualQuestionsList = useMemo(
    () => normalizeIndividualSignupQuestions(currentProduct?.individual_signup_questions),
    [currentProduct?.individual_signup_questions]
  );

  // Only sync keys when a product is resolved — avoids wiping draft answers before `products` loads.
  useEffect(() => {
    if (!currentProduct) return;
    const qs = normalizeIndividualSignupQuestions(currentProduct.individual_signup_questions);
    setIndividualSignupAnswers((prev) => {
      const next = {};
      for (const q of qs) next[q] = prev[q] ?? "";
      return next;
    });
  }, [currentProduct]);

  // Keep region/division/query in sync with the selected product (correct city + division label).
  useEffect(() => {
    if (!products.length || !productId) return;
    const hit = products.find((p) => String(p.id) === String(productId));
    if (!hit) return;
    const divLabel = divisionLabelForProduct(hit.city, hit.division, hit);
    const cityMismatch = normalizeCity(hit.city) !== normalizeCity(region);
    const divMismatch = division !== divLabel;
    if (!cityMismatch && !divMismatch) return;
    setRegion(hit.city);
    setDivision(divLabel);
    const next = new URLSearchParams(searchParams);
    next.set("region", hit.city);
    next.set("division", divLabel);
    next.set("productId", String(hit.id));
    next.delete("browseSport");
    setSearchParams(next, { replace: true });
  }, [products, productId, region, division, searchParams, setSearchParams]);

  useEffect(() => {
    logRegistrationEvent("reg_apply_step_view", {
      step_index: step,
      step_name: APPLY_STEP_NAMES[step] ?? String(step),
      has_product: Boolean(productId || currentProduct?.id),
      region: region || undefined,
      division: division || undefined,
    });
  }, [step, region, division, productId, currentProduct?.id]);

  const productsForWizardDivision = useMemo(() => {
    if (!region || !division) return [];
    return products.filter(
      (p) =>
        normalizeCity(p.city) === normalizeCity(region) &&
        divisionLabelForProduct(p.city, p.division, p) === division
    );
  }, [products, region, division]);

  const browseSportOptions = useMemo(() => {
    const uniq = new Set();
    for (const p of productsForWizardDivision) {
      const s = String(p.sport ?? "").trim();
      if (s) uniq.add(s);
    }
    return [...uniq].sort((a, b) => a.localeCompare(b));
  }, [productsForWizardDivision]);

  const productsForWizardPick = useMemo(() => {
    if (!wizardBrowseSport) return productsForWizardDivision;
    return productsForWizardDivision.filter((p) => String(p.sport ?? "").trim() === wizardBrowseSport);
  }, [productsForWizardDivision, wizardBrowseSport]);

  const needsWizardProductPick =
    step === 2 && !productId && productsForWizardDivision.length > 1;

  const onWizardSportChange = useCallback(
    (sport) => {
      setWizardBrowseSport(sport);
      const next = new URLSearchParams(searchParams);
      if (sport) next.set("browseSport", sport);
      else next.delete("browseSport");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const selectWizardProduct = useCallback(
    (prod) => {
      if (!prod?.id) return;
      const c = String(prod.city ?? "").trim() || region;
      const divLabel = divisionLabelForProduct(c, prod.division, prod);
      setProductId(String(prod.id));
      setRegion(c);
      setDivision(divLabel);
      setWizardBrowseSport("");
      const next = new URLSearchParams(searchParams);
      next.set("region", c);
      next.set("division", divLabel);
      next.set("productId", String(prod.id));
      next.delete("browseSport");
      setSearchParams(next, { replace: true });
      logRegistrationEvent("reg_wizard_program_selected", {
        product_id: String(prod.id),
        region: c,
        division: divLabel,
      });
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      });
    },
    [region, searchParams, setSearchParams]
  );

  const copyShareLink = async () => {
    if (!currentProduct?.id) return;
    const c = currentProduct.city || region;
    const divForUrl = divisionLabelForProduct(c, currentProduct.division, currentProduct);
    const url = buildRegistrationProductUrl(
      typeof window !== "undefined" ? window.location.origin : "",
      c,
      divForUrl,
      currentProduct.id
    );
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      logRegistrationEvent("reg_copy_share_link", { product_id: String(currentProduct.id) });
      window.setTimeout(() => setLinkCopied(false), 2500);
    } catch (_) {
      // ignore
    }
  };

  const teamAllowed =
    currentProduct?.team_registeration === true || currentProduct?.team_registration === true;
  const individualAllowed = currentProduct?.individual_registeration === true || currentProduct?.individual_registration === true;
  const isCaptainOnlyRegistration = currentProduct?.captain_only_registration === true;
  const requiresEmailConsent = currentProduct?.require_email_consent === true;
  const allowedRegistrationTypes = [
    teamAllowed && "team",
    individualAllowed && "individual",
  ].filter(Boolean);
  const paidCount = useMemo(() => {
    if (currentProduct) {
      return paidCountsByKey[paidCountKeyForProduct(currentProduct)] ?? 0;
    }
    if (!region) return 0;
    return division ? (paidCountsByKey[`${region}__${division}`] || 0) : 0;
  }, [region, division, currentProduct, paidCountsByKey]);
  const rawCapacity = normalizeNumber(currentProduct?.capacity);
  const remainingCapacity = rawCapacity == null ? null : Math.max(0, Math.floor(rawCapacity) - paidCount);
  const isSoldOut = currentProduct?.sold_out === true || remainingCapacity === 0;
  // (registrationType is used directly for the UI; kept no extra derived data)

  useEffect(() => {
    if ((step === 2 || step === 3) && allowedRegistrationTypes.length === 1) {
      setRegistrationType(allowedRegistrationTypes[0]);
    } else if (step === 2 && allowedRegistrationTypes.length && !allowedRegistrationTypes.includes(registrationType)) {
      setRegistrationType(allowedRegistrationTypes[0]);
    }
  }, [step, registrationType, allowedRegistrationTypes]);

  useEffect(() => {
    if (currentProduct) {
      setErrors((prev) => (prev.product ? { ...prev, product: null } : prev));
    }
  }, [currentProduct]);

  const validateStep = () => {
    const e = {};
    if (step === 0) {
      if (!region) e.region = "Select a region";
    } else if (step === 1) {
      if (!division) e.division = "Select a division";
    } else if (step === 2) {
      if (!region) e.region = "Select a region";
      if (!division) e.division = "Select a division";
      if (cityProducts.length > 0 && !currentProduct) {
        e.product = "Choose a product on the products page or use a registration link.";
      }
      if (isSoldOut) e.checkout = "This division is sold out. Please choose another division.";
    } else if (step === 3 && registrationType === "team") {
      if (isSoldOut) e.checkout = "This division is sold out. Please choose another division.";
      if (!teamName?.trim()) e.teamName = "Enter team name";
      if (!captainName?.trim()) e.captainName = "Captain name required";
      if (!captainPhone?.trim()) e.captainPhone = "Captain phone required";
      if (requiresEmailConsent && !emailContactConsent) {
        e.emailContactConsent = "You must consent to storing your email for contact before registering.";
      }
      if (!isCaptainOnlyRegistration) {
        const emails = players.map((p) => p.email?.toLowerCase().trim()).filter(Boolean);
        const jerseys = players.map((p) => p.jersey_number?.toString().trim()).filter(Boolean);
        const emailSet = new Set(emails);
        const jerseySet = new Set(jerseys);
        if (emailSet.size !== emails.length) e.players = "All player emails must be unique";
        if (jerseySet.size !== jerseys.length) e.players = (e.players || "") + " All jersey numbers must be unique.";
        players.forEach((p, i) => {
          if (!p.name?.trim()) e[`player_name_${i}`] = "Name required";
          if (!p.email?.trim()) e[`player_email_${i}`] = "Email required";
          else if (!isValidEmail(p.email)) e[`player_email_${i}`] = "Enter a valid email address";
          if (p.jersey_number === "" || p.jersey_number == null) e[`player_jersey_${i}`] = "Jersey number required";
          if (!p.jersey_size) e[`player_size_${i}`] = "Size required";
        });
      }
    } else if (step === 3 && registrationType === "individual") {
      if (isSoldOut) e.checkout = "This division is sold out. Please choose another division.";
      const qs = normalizeIndividualSignupQuestions(currentProduct?.individual_signup_questions);
      if (qs.length) {
        Object.assign(e, validateIndividualSignupAnswers(qs, individualSignupAnswers));
      } else {
        if (!captainName?.trim()) e.captainName = "Name required";
        if (!captainPhone?.trim()) e.captainPhone = "Phone number required";
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    if (step === 3 && !user) {
      logRegistrationEvent("reg_apply_auth_redirect", {
        registration_type: registrationType,
        product_id: productId ? String(productId) : undefined,
      });
      const draftKey = `hadaf_register_draft_${Date.now()}`;
      try {
        localStorage.setItem(
          draftKey,
          JSON.stringify({
            region,
            division,
            productId,
            registrationType,
            step: 3,
            teamName,
            captainName,
            captainPhone,
            emailContactConsent,
            players,
            individualSignupAnswers,
            wizardBrowseSport,
          })
        );
      } catch (_) {
        // fallback for environments where localStorage isn't available
        sessionStorage.setItem(
          draftKey,
          JSON.stringify({
            region,
            division,
            productId,
            registrationType,
            step: 3,
            teamName,
            captainName,
            captainPhone,
            emailContactConsent,
            players,
            individualSignupAnswers,
            wizardBrowseSport,
          })
        );
      }
      const returnTo = `/register/apply?draftKey=${encodeURIComponent(draftKey)}`;
      navigate(`/register?return_to=${encodeURIComponent(returnTo)}`);
      return;
    }
    if (step < 3) {
      if (step === 2) {
        logRegistrationEvent("reg_step2_complete", {
          registration_type: registrationType,
          product_id: productId ? String(productId) : undefined,
        });
      }
      logRegistrationEvent("reg_apply_continue", { from_step: step, to_step: step + 1 });
      setStep((s) => s + 1);
    } else if (PAYMENTS_ENABLED) handlePayment();
    else handleSubmitNoPayment();
  };

  const handleSubmitNoPayment = async () => {
    setSubmitLoading(true);
    setErrors({});
    try {
      const team_name = registrationType === "team" ? teamName.trim() : "Individual";
      const city = currentProduct?.city || region;
      const divForTeam = currentProduct
        ? divisionLabelForProduct(currentProduct.city, currentProduct.division, currentProduct)
        : division;
      const regSport = String(currentProduct?.sport ?? "Soccer").trim() || "Soccer";
      const regAgeGroup = String(currentProduct?.age_group ?? "Adult").trim() || "Adult";
      const qsInd = normalizeIndividualSignupQuestions(currentProduct?.individual_signup_questions);
      const guardianName = signupAnswerGuardianName(individualSignupAnswers, qsInd);
      const guardianPhone = signupAnswerGuardianPhone(individualSignupAnswers, qsInd);
      const captainDisplay =
        registrationType === "individual" && qsInd.length
          ? guardianName || captainName?.trim() || user?.user_metadata?.full_name || "Registrant"
          : captainName?.trim() || user?.user_metadata?.full_name || (registrationType === "team" ? "Captain" : "Registrant");
      const captainPhoneOut =
        registrationType === "individual" && qsInd.length ? guardianPhone || captainPhone.trim() : captainPhone.trim();

      if (registrationType === "team" && team_name && (isCaptainOnlyRegistration || players.length > 0)) {
        const { data: teamRow, error: teamErr } = await supabase
          .from("league_teams")
          .insert({
            name: team_name,
            region: city,
            division: divForTeam,
            captain_id: user.id,
            payment_status: "pending",
            sport: regSport,
            age_group: regAgeGroup,
            captain_name: captainDisplay,
            captain_phone: captainPhoneOut,
            pending_captain_email: user?.email ?? null,
            email_contact_consent: requiresEmailConsent ? emailContactConsent : null,
            product_id: productId ?? currentProduct?.id ?? null,
          })
          .select("id")
          .single();
        if (teamErr) throw new Error(teamErr.message);
        const teamId = teamRow?.id;
        if (teamId && !isCaptainOnlyRegistration) {
          for (const p of players) {
            if (!p.email?.trim() || !p.name?.trim()) continue;
            const num = p.jersey_number;
            if (num === "" || num === undefined || num === null) continue;
            await supabase.from("league_team_players").insert({
              team_id: teamId,
              email: p.email.trim().toLowerCase(),
              name: (p.name ?? "").trim(),
              jersey_number: String(num),
              jersey_size: (p.jersey_size ?? "").trim() || null,
              sport: regSport,
              age_group: regAgeGroup,
            });
          }
        }
      }

      // Admin email + roster invites: complete-registration (preferred). Fallback = send-registration-email (admin only).
      const registrationNotifyBody = {
        captain_email: user?.email ?? "",
        captain_name: captainDisplay,
        captain_phone: captainPhoneOut,
        team_name: registrationType === "team" ? teamName.trim() : "Individual",
        city: currentProduct?.city || region,
        division: currentProduct ? divisionLabelForProduct(currentProduct.city, currentProduct.division, currentProduct) : division,
        registration_type: registrationType,
        players:
          registrationType === "team" && !isCaptainOnlyRegistration
            ? players.map((p) => ({
              name: p.name,
              email: p.email,
              jersey_number: p.jersey_number,
              jersey_size: p.jersey_size || null,
              instagram: p.instagram || null,
            }))
            : [],
        product: currentProduct || null,
        ...(qsInd.length ? { individual_signup_answers: individualSignupAnswers } : {}),
        ...(requiresEmailConsent ? { email_contact_consent: emailContactConsent } : {}),
      };
      try {
        const { error: completeErr } = await supabase.functions.invoke("complete-registration", {
          body: { email_only: true, ...registrationNotifyBody },
        });
        if (completeErr) {
          const { error: adminErr } = await supabase.functions.invoke("send-registration-email", {
            body: registrationNotifyBody,
          });
          if (adminErr) {
            console.warn("Admin notification failed (complete-registration and send-registration-email):", completeErr, adminErr);
          }
        }
      } catch (e) {
        try {
          const { error: adminErr } = await supabase.functions.invoke("send-registration-email", {
            body: registrationNotifyBody,
          });
          if (adminErr) console.warn("Admin notification failed:", adminErr);
        } catch (e2) {
          console.warn("Admin notification failed:", e, e2);
        }
      }

      logRegistrationEvent("reg_register_submit_free", {
        registration_type: registrationType,
        product_id: productId ? String(productId) : undefined,
      });
      navigate("/register/success", { replace: true, state: { noPayment: true } });
    } catch (err) {
      const msg = err?.message ? String(err.message).slice(0, 120) : "unknown";
      logRegistrationEvent("reg_register_submit_failed", {
        registration_type: registrationType,
        product_id: productId ? String(productId) : undefined,
        error_detail: msg,
      });
      setErrors({ checkout: err.message || "Registration failed. Please try again." });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleBack = () =>
    setStep((s) => {
      const next = Math.max(0, s - 1);
      if (next !== s) logRegistrationEvent("reg_apply_back", { from_step: s, to_step: next });
      return next;
    });

  const addPlayer = () => setPlayers((p) => [...p, initialPlayer()]);
  const removePlayer = (index) => setPlayers((p) => p.filter((_, i) => i !== index));
  const updatePlayer = (index, field, value) => {
    setPlayers((p) => {
      const next = [...p];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handlePayment = async () => {
    setCheckoutLoading(true);
    setErrors({});
    try {
      if (!user?.id) {
        setErrors({ checkout: "Sign in or create an account to continue to payment." });
        return;
      }
      const qsPay = normalizeIndividualSignupQuestions(currentProduct?.individual_signup_questions);
      if (registrationType === "individual" && qsPay.length) {
        const signupErrs = validateIndividualSignupAnswers(qsPay, individualSignupAnswers);
        if (Object.keys(signupErrs).length) {
          setErrors(signupErrs);
          return;
        }
      }
      if (requiresEmailConsent && !emailContactConsent) {
        setErrors({
          emailContactConsent: "You must consent to storing your email for contact before registering.",
        });
        return;
      }
      const gName = signupAnswerGuardianName(individualSignupAnswers, qsPay);
      const gPhone = signupAnswerGuardianPhone(individualSignupAnswers, qsPay);
      const payload = {
        region: currentProduct?.city || region,
        division: currentProduct
          ? divisionLabelForProduct(currentProduct.city, currentProduct.division, currentProduct)
          : division,
        product_id: productId ?? currentProduct?.id ?? null,
        registration_type: registrationType,
        team_name: registrationType === "team" ? teamName : null,
        captain_name:
          qsPay.length && registrationType === "individual"
            ? gName || captainName?.trim() || user?.user_metadata?.full_name || null
            : captainName?.trim() || user?.user_metadata?.full_name || null,
        captain_phone:
          qsPay.length && registrationType === "individual"
            ? gPhone || captainPhone?.trim() || null
            : captainPhone?.trim() || null,
        sport: currentProduct?.sport ?? "Soccer",
        age_group: currentProduct?.age_group ?? "Adult",
        players: registrationType === "team" && !isCaptainOnlyRegistration ? players : [],
        email_contact_consent: requiresEmailConsent ? emailContactConsent : null,
        ...(qsPay.length && registrationType === "individual"
          ? { individual_signup_answers: individualSignupAnswers }
          : {}),
      };
      const { data, error } = await supabase.functions.invoke("create-checkout", { body: payload });
      if (error) {
        let detailMsg = error.message || "Could not start checkout";
        try {
          // Supabase Functions errors may include a Response-like context with JSON body.
          const parsed = await error.context?.json?.();
          if (parsed?.error) {
            detailMsg = parsed.error;
            if (parsed?.code || parsed?.trace_id) {
              detailMsg += ` (${[parsed.code, parsed.trace_id].filter(Boolean).join(" | ")})`;
            }
          }
        } catch (_) {
          // ignore parse issues, keep base message
        }
        throw new Error(detailMsg);
      }
      const url = data?.url;
      if (url) {
        logRegistrationEvent("reg_checkout_started", {
          product_id: String(productId ?? currentProduct?.id ?? ""),
          registration_type: registrationType,
        });
        window.location.href = url;
      } else throw new Error("No checkout URL returned.");
    } catch (err) {
      console.error("create-checkout failed", err);
      logRegistrationEvent("reg_checkout_start_failed", {
        registration_type: registrationType,
        product_id: productId ? String(productId) : undefined,
        error_detail: err?.message ? String(err.message).slice(0, 120) : "unknown",
      });
      setErrors({ checkout: err.message || "Payment setup failed." });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const emailConsentBlock = requiresEmailConsent ? (
    <Box
      sx={{
        mb: 2.5,
        p: 2,
        borderRadius: "12px",
        border: errors.emailContactConsent
          ? "1px solid #fca5a5"
          : "1px solid rgba(15,23,42,0.12)",
        backgroundColor: "rgba(248,250,252,0.95)",
      }}
    >
      <Checkbox
        name="emailContactConsent"
        required
        checked={emailContactConsent}
        onChange={(v) => {
          setEmailContactConsent(v);
          if (v) setErrors((prev) => ({ ...prev, emailContactConsent: null }));
        }}
        label="I consent to Hadaf storing my account email for league contact"
        finePrint={EMAIL_CONSENT_FINE_PRINT}
        error={!!errors.emailContactConsent}
        helperText={errors.emailContactConsent}
      />
      {user?.email && (
        <Typography
          sx={{
            fontFamily: REG_FONT,
            fontSize: "0.75rem",
            color: "#475569",
            mt: 1.25,
          }}
        >
          Email on your account: <strong>{user.email}</strong>
        </Typography>
      )}
    </Box>
  ) : null;
  const lastStepLabel = PAYMENTS_ENABLED
    ? (registrationType === "team"
        ? (isCaptainOnlyRegistration ? "Team & captain details" : "Team name & roster")
        : "Proceed to payment")
    : (registrationType === "team"
        ? (isCaptainOnlyRegistration ? "Team & captain details" : "Team name & roster")
        : "Your details");
  const stepLabels = ["Select region", "Select division", "Individual or team", lastStepLabel];

  if (loading) return null;

  const stepIntro = [
    "Choose the city where you’ll play.",
    "Pick your division — location, ice times, and fee for this product appear below.",
    "You already chose your league. Pick team or individual registration next.",
    registrationType === "team"
      ? (isCaptainOnlyRegistration
          ? "Enter your team name, captain contact, and consent to store your account email for league contact."
          : "Enter your team name, captain contact, and full roster (unique emails & jersey numbers).")
      : individualQuestionsList.length
        ? PAYMENTS_ENABLED
          ? "Complete every field below, then continue to our secure payment page."
          : "Complete every field below, then click Submit to finish."
        : PAYMENTS_ENABLED
          ? "Confirm your name and phone, then continue to our secure payment page."
          : "Confirm your name and phone, then click Submit to complete registration.",
  ];

  const showHero = Boolean((currentProduct?.image || "").trim());

  return (
    <Box
      sx={{
        position: "relative",
        isolation: "isolate",
        minHeight: "100vh",
        width: "100%",
        fontFamily: REG_FONT,
      }}
    >
      {showHero ? (
        <>
          <Box
            aria-hidden
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 0,
              backgroundColor: "#0f172a",
              backgroundImage: `url(${currentProduct.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center 22%",
            }}
          />
          <Box
            aria-hidden
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 1,
              pointerEvents: "none",
              background: [
                "linear-gradient(180deg, rgba(2,6,23,0.94) 0%, rgba(2,6,23,0.55) 22%, rgba(2,6,23,0.2) 45%, rgba(2,6,23,0.25) 55%, rgba(2,6,23,0.88) 100%)",
              ].join(","),
            }}
          />
          <Box
            aria-hidden
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              height: { xs: "min(42vh, 320px)", sm: "min(38vh, 360px)" },
              zIndex: 1,
              pointerEvents: "none",
              background: "linear-gradient(180deg, rgba(2,6,23,0.97) 0%, rgba(2,6,23,0.72) 45%, transparent 100%)",
            }}
          />
          <Box
            aria-hidden
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              height: { xs: "min(36vh, 280px)", sm: "min(32vh, 300px)" },
              zIndex: 1,
              pointerEvents: "none",
              background: "linear-gradient(0deg, rgba(2,6,23,0.96) 0%, rgba(2,6,23,0.65) 50%, transparent 100%)",
            }}
          />
        </>
      ) : (
        <Box
          aria-hidden
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 0,
            background: "linear-gradient(165deg, #e2e8f0 0%, #f1f5f9 40%, #f8fafc 100%)",
          }}
        />
      )}

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          maxWidth: { xs: "100%", sm: 720, md: 840 },
          mx: "auto",
          pt: { xs: 2, sm: 4 },
          pb: { xs: 8, sm: 10 },
          px: { xs: 1.5, sm: 2 },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
          <Button
            component={Link}
            to="/register/select-division"
            variant="text"
            startIcon={<ArrowBackIcon sx={{ color: showHero ? "rgba(255,255,255,0.85)" : undefined }} />}
            sx={{
              fontFamily: REG_FONT,
              color: showHero ? "rgba(255,255,255,0.92)" : "#0f172a",
              fontWeight: 600,
              fontSize: "0.875rem",
              textTransform: "none",
              borderRadius: 2,
              px: 1,
              "&:hover": {
                backgroundColor: showHero ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.04)",
              },
            }}
          >
            Back to products
          </Button>
        </Box>

        <Box sx={{ textAlign: "center", mb: { xs: 2.5, sm: 3 } }}>
          <Typography
            variant="h5"
            component="h1"
            sx={{
              fontFamily: REG_FONT,
              color: showHero ? "#f8fafc" : "#0f172a",
              fontWeight: 700,
              letterSpacing: "-0.03em",
              fontSize: { xs: "1.5rem", sm: "1.75rem" },
              lineHeight: 1.2,
              textShadow: showHero ? "0 2px 24px rgba(0,0,0,0.45)" : "none",
            }}
          >
            League registration
          </Typography>
          <Typography
            sx={{
              fontFamily: REG_FONT,
              color: showHero ? "rgba(248,250,252,0.82)" : "#64748b",
              mt: 1.25,
              maxWidth: 520,
              mx: "auto",
              px: { xs: 0.5, sm: 0 },
              lineHeight: 1.6,
              fontSize: "0.9375rem",
              fontWeight: 400,
              textShadow: showHero ? "0 1px 16px rgba(0,0,0,0.5)" : "none",
            }}
          >
            {stepIntro[step] ?? "Complete the steps below to join HADAF."}
          </Typography>
        </Box>
        <Stepper
          activeStep={step}
          orientation={isNarrow ? "vertical" : "horizontal"}
          alternativeLabel={!isNarrow}
          sx={{
            mb: 3,
            alignItems: isNarrow ? "stretch" : "center",
            fontFamily: REG_FONT,
            "& .MuiStepLabel-label": {
              fontFamily: REG_FONT,
              color: showHero ? "rgba(248,250,252,0.72)" : "#64748b",
              fontSize: { xs: "0.8125rem", sm: "0.75rem" },
              whiteSpace: "normal",
              lineHeight: 1.35,
              fontWeight: 500,
            },
            "& .Mui-completed": { color: showHero ? "#f8fafc" : "#0f172a" },
            "& .Mui-active .MuiStepLabel-label": {
              color: "#0f172a",
              fontWeight: 600,
            },
            "& .MuiStepIcon-root": {
              color: showHero ? "rgba(255,255,255,0.35)" : undefined,
            },
            "& .MuiStepIcon-root.Mui-active": showHero
              ? {
                  color: "#ffffff",
                  "& circle": {
                    fill: "#ffffff",
                    stroke: "rgba(15,23,42,0.2)",
                  },
                  "& .MuiStepIcon-text": {
                    fill: "#0f172a",
                  },
                }
              : {},
            "& .MuiStepIcon-root.Mui-completed": { color: showHero ? "rgba(255,255,255,0.9)" : undefined },
          }}
        >
          {stepLabels.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.25, sm: 3.5 },
            fontFamily: REG_FONT,
            backgroundColor: showHero ? "rgba(248,250,252,0.9)" : "#ffffff",
            backdropFilter: showHero ? "blur(20px)" : "none",
            WebkitBackdropFilter: showHero ? "blur(20px)" : "none",
            color: "#0f172a",
            border: showHero ? "1px solid rgba(255,255,255,0.55)" : "1px solid rgba(15,23,42,0.08)",
            borderRadius: "14px",
            boxShadow: showHero
              ? "0 25px 50px -12px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.06) inset"
              : "0 12px 40px rgba(15,23,42,0.08)",
          }}
        >
        {errors.checkout && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrors((e) => ({ ...e, checkout: null }))}>
            {errors.checkout}
          </Alert>
        )}
        {step === 0 && (
          <FormControl fullWidth error={!!errors.region} sx={{ mb: 2 }}>
            <InputLabel>Region</InputLabel>
            <Select
              value={region}
              onChange={(e) => {
                const next = new URLSearchParams(searchParams);
                next.delete("productId");
                next.delete("sport");
                setSearchParams(next, { replace: true });
                const r = e.target.value;
                setRegion(r);
                logRegistrationEvent("reg_apply_region_selected", { region: r });
                setDivision("");
                setProductId(null);
                setWizardBrowseSport("");
              }}
              label="Region"
              size="small"
            >
              {REGIONS.map((r) => (
                <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {step === 1 && (
          <Box>
            <FormControl fullWidth error={!!errors.division} sx={{ mb: 2 }}>
              <InputLabel>Division</InputLabel>
              <Select
                value={division}
                onChange={(e) => {
                  const next = new URLSearchParams(searchParams);
                  next.delete("productId");
                  next.delete("sport");
                  setSearchParams(next, { replace: true });
                  const d = e.target.value;
                  setDivision(d);
                  logRegistrationEvent("reg_apply_division_selected", { region, division: d });
                  setProductId(null);
                  setWizardBrowseSport("");
                }}
                label="Division"
                size="small"
              >
                {divisions.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {division && (() => {
              const list = cityProducts.filter(
                (p) => divisionLabelForProduct(region, p.division, p) === division
              );
              if (!list.length) {
                return (
                  <Typography variant="body2" sx={{ color: "#666", mt: 2 }}>
                    No registration products for this division yet.
                  </Typography>
                );
              }
              if (list.length === 1) {
                const prod = list[0];
                const timingStr = Array.isArray(prod.timing) && prod.timing.length ? prod.timing.join(", ") : "TBD";
                const paidC = region && division ? (paidCountsByKey[`${region}__${division}`] || 0) : 0;
                const rawCap = normalizeNumber(prod.capacity);
                const rem = rawCap == null ? null : Math.max(0, Math.floor(rawCap) - paidC);
                const sold = prod.sold_out === true || rem === 0;
                return (
                  <Box
                    sx={{
                      mt: 2,
                      p: { xs: 2, sm: 2.5 },
                      borderRadius: "12px",
                      border: "1px solid rgba(15,23,42,0.09)",
                      background: "linear-gradient(165deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.99) 100%)",
                    }}
                  >
                    <RegSectionLabel>
                      {productTypeOf(prod) === "tournament" ? "Tournament details" : "League details"}
                    </RegSectionLabel>
                    <RegMeta label="Location">{prod.location || "TBD"}</RegMeta>
                    <RegMeta label="Schedule">{timingStr}</RegMeta>
                    {prod.description && (
                      <Markdown source={toProductMarkdown(prod.description)} dense sx={{ mb: 2, color: "#334155" }} />
                    )}
                    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 1, pt: 1, borderTop: "1px dashed rgba(15,23,42,0.1)" }}>
                      <Typography sx={{ fontFamily: REG_FONT, fontSize: "0.8125rem", fontWeight: 600, color: "#64748b" }}>Fee</Typography>
                      <Typography sx={{ fontFamily: REG_FONT, fontSize: "1.0625rem", fontWeight: 700, color: "#0f172a" }}>{formatCost(prod.cost)}</Typography>
                    </Box>
                    {sold && (
                      <Typography sx={{ fontFamily: REG_FONT, fontSize: "0.875rem", color: "#b91c1c", fontWeight: 600, mt: 1.5 }}>
                        Sold out
                      </Typography>
                    )}
                  </Box>
                );
              }
              return (
                <Box
                  sx={{
                    mt: 2,
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: "12px",
                    border: "1px solid rgba(15,23,42,0.09)",
                    background: "linear-gradient(165deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.99) 100%)",
                  }}
                >
                  <RegSectionLabel>Multiple programs</RegSectionLabel>
                  <RegTitle sx={{ mb: 1 }}>Leagues in this division</RegTitle>
                  <RegBody sx={{ mb: 1.5 }}>
                    Multiple programs share this division.{" "}
                    <Button
                      component={Link}
                      to="/register/select-division"
                      sx={{
                        p: 0,
                        minWidth: 0,
                        textTransform: "none",
                        fontWeight: 600,
                        fontFamily: REG_FONT,
                        fontSize: "inherit",
                        color: "#0f172a",
                        verticalAlign: "baseline",
                      }}
                    >
                      Pick your league on the products page
                    </Button>
                    .
                  </RegBody>
                  <Box component="ul" sx={{ m: 0, pl: 2.25, mt: 0.5, "& li": { mb: 0.75 } }}>
                    {list.map((prod) => (
                      <li key={prod.id}>
                        <Typography sx={{ fontFamily: REG_FONT, fontSize: "0.875rem", color: "#334155", lineHeight: 1.55 }}>
                          <Box component="span" sx={{ fontWeight: 600, color: "#0f172a" }}>{prod.name || "League"}</Box>
                          {prod.sport ? ` · ${prod.sport}` : ""}
                          {prod.age_group ? ` · ${prod.age_group}` : ""}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                </Box>
              );
            })()}
          </Box>
        )}
        {step === 2 && (
          <Box>
            {errors.product && (
              <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setErrors((prev) => ({ ...prev, product: null }))}>
                {errors.product}
              </Alert>
            )}
            {needsWizardProductPick && (
              <Box sx={{ mb: 2.5 }}>
                <RegSectionLabel>Choose program</RegSectionLabel>
                <RegTitle sx={{ mb: 1 }}>Pick your league</RegTitle>
                <RegBody sx={{ mb: 2 }}>
                  Several programs match {region} – {division}. Filter by sport, then choose the league you want.
                </RegBody>
                {browseSportOptions.length > 1 && (
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Sport</InputLabel>
                    <Select
                      value={wizardBrowseSport}
                      label="Sport"
                      onChange={(e) => onWizardSportChange(e.target.value)}
                    >
                      <MenuItem value="">All sports</MenuItem>
                      {browseSportOptions.map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {productsForWizardPick.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    No leagues match this sport filter.
                  </Typography>
                ) : (
                  <FormControl fullWidth size="small" error={!!errors.product}>
                    <InputLabel id="wizard-league-select" shrink>
                      League / program
                    </InputLabel>
                    <Select
                      labelId="wizard-league-select"
                      value=""
                      label="League / program"
                      displayEmpty
                      renderValue={() => (
                        <Typography variant="body2" sx={{ color: "#999" }}>Choose a program…</Typography>
                      )}
                      onChange={(e) => {
                        const id = e.target.value;
                        const prod = productsForWizardPick.find((p) => String(p.id) === String(id));
                        if (prod) selectWizardProduct(prod);
                      }}
                    >
                      <MenuItem value="" disabled>
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          Choose a program…
                        </Typography>
                      </MenuItem>
                      {productsForWizardPick.map((prod) => {
                        const paidC = paidCountsByKey[paidCountKeyForProduct(prod)] || 0;
                        const rawCap = normalizeNumber(prod.capacity);
                        const rem = rawCap == null ? null : Math.max(0, Math.floor(rawCap) - paidC);
                        const isSoldOut = prod.sold_out === true || (rem != null && rem <= 0);
                        const secondary = [prod.sport, prod.age_group, formatCost(prod.cost)].filter(Boolean).join(" · ");
                        return (
                          <MenuItem key={prod.id} value={String(prod.id)} disabled={isSoldOut}>
                            <ListItemText
                              primary={prod.name || `${region} – ${division}`}
                              secondary={secondary}
                              primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                              secondaryTypographyProps={{ variant: "caption", sx: { whiteSpace: "normal" } }}
                            />
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {errors.product && <FormHelperText>{errors.product}</FormHelperText>}
                  </FormControl>
                )}
                <Typography sx={{ fontFamily: REG_FONT, fontSize: "0.8125rem", color: "#64748b", display: "block", mt: 1.5, lineHeight: 1.55 }}>
                  Or browse every city on the{" "}
                  <Button
                    component={Link}
                    to="/register/select-division"
                    sx={{
                      p: 0,
                      minWidth: 0,
                      textTransform: "none",
                      fontWeight: 600,
                      verticalAlign: "baseline",
                      fontFamily: REG_FONT,
                      fontSize: "inherit",
                      color: "#0f172a",
                    }}
                  >
                    products page
                  </Button>
                  .
                </Typography>
              </Box>
            )}
            {!currentProduct && region && division && !needsWizardProductPick && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" component="span">
                  Choose your league on the{" "}
                  <Button
                    component={Link}
                    to="/register/select-division"
                    sx={{ p: 0, minWidth: 0, textTransform: "none", fontWeight: 700, verticalAlign: "baseline" }}
                  >
                    products page
                  </Button>
                  , or open a registration link you were sent.
                </Typography>
              </Alert>
            )}
            {region && division && (
              <Box
                sx={{
                  mb: 2.5,
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: "12px",
                  border: "1px solid rgba(15,23,42,0.09)",
                  background: "linear-gradient(165deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.98) 100%)",
                  boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
                }}
              >
                <RegSectionLabel>Your selection</RegSectionLabel>
                <RegTitle sx={{ mb: currentProduct?.name ? 0.5 : 1.5 }}>
                  {currentProduct
                    ? `${currentProduct.city} – ${divisionLabelForProduct(currentProduct.city, currentProduct.division, currentProduct)}`
                    : `${region} – ${division}`}
                </RegTitle>
                {currentProduct && (
                  <>
                    {currentProduct.name && (
                      <RegBody sx={{ color: "#334155", fontWeight: 500, mb: 2, mt: 0 }}>
                        {currentProduct.name}
                      </RegBody>
                    )}
                    <Divider sx={{ borderColor: "rgba(15,23,42,0.08)", mb: 2 }} />
                    <RegMeta label="Location">{currentProduct.location || "TBD"}</RegMeta>
                    <RegMeta label="Schedule">
                      {Array.isArray(currentProduct.timing) && currentProduct.timing.length
                        ? currentProduct.timing.join(", ")
                        : "TBD"}
                    </RegMeta>
                    {currentProduct.description && (
                      <Markdown source={toProductMarkdown(currentProduct.description)} dense sx={{ mb: 2, mt: -0.5, color: "#334155" }} />
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "baseline",
                        gap: 1,
                        pt: 0.5,
                        borderTop: "1px dashed rgba(15,23,42,0.1)",
                      }}
                    >
                      <Typography sx={{ fontFamily: REG_FONT, fontSize: "0.8125rem", fontWeight: 600, color: "#64748b" }}>
                        Fee
                      </Typography>
                      <Typography sx={{ fontFamily: REG_FONT, fontSize: "1.0625rem", fontWeight: 700, color: "#0f172a" }}>
                        {formatCost(currentProduct.cost)}
                      </Typography>
                    </Box>
                    {isSoldOut && (
                      <Typography
                        sx={{
                          fontFamily: REG_FONT,
                          fontSize: "0.875rem",
                          color: "#b91c1c",
                          fontWeight: 600,
                          mt: 1.5,
                        }}
                      >
                        Sold out
                      </Typography>
                    )}
                    <TextField
                      fullWidth
                      size="small"
                      label="Share this registration link"
                      value={
                        typeof window !== "undefined"
                          ? buildRegistrationProductUrl(
                              window.location.origin,
                              currentProduct.city || region,
                              divisionLabelForProduct(currentProduct.city || region, currentProduct.division, currentProduct),
                              currentProduct.id
                            )
                          : ""
                      }
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={copyShareLink} edge="end" aria-label="Copy registration link" size="small">
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mt: 2.5,
                        "& .MuiInputLabel-root": { fontFamily: REG_FONT, fontSize: "0.8125rem" },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          fontFamily: REG_FONT,
                          fontSize: "0.8125rem",
                          backgroundColor: "#fff",
                        },
                      }}
                    />
                    {linkCopied && (
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: REG_FONT, color: "success.main", display: "block", mt: 0.75, fontWeight: 500 }}
                      >
                        Copied to clipboard
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            )}
            {currentProduct && allowedRegistrationTypes.length === 1 && (
              <RegBody sx={{ color: "#475569", mb: 2.5, fontWeight: 500 }}>
                {allowedRegistrationTypes[0] === "team"
                  ? (isCaptainOnlyRegistration
                      ? "This league is team registration only — you’ll enter team name and captain contact next (no full roster yet)."
                      : "This league is team registration only — you’ll register once as captain and add your full roster next.")
                  : "This league is individual registration only — you’ll enter your own details next."}
              </RegBody>
            )}
            {currentProduct && allowedRegistrationTypes.length > 1 && (
              <>
                <RegSectionLabel>Registration type</RegSectionLabel>
                <FormLabel sx={{ mb: 1.25, display: "block", fontFamily: REG_FONT, fontSize: "1rem", fontWeight: 600, color: "#0f172a" }}>
                  How do you want to register?
                </FormLabel>
                <RadioGroup
                  value={registrationType}
                  onChange={(e) => {
                    const v = e.target.value;
                    setRegistrationType(v);
                    logRegistrationEvent("reg_registration_type", { registration_type: v });
                  }}
                  sx={{ color: "#0f172a" }}
                >
                  {REGISTRATION_OPTIONS.filter((opt) => {
                    if (opt.value === "team") {
                      return currentProduct.team_registeration === true || currentProduct.team_registration === true;
                    }
                    return currentProduct.individual_registeration === true || currentProduct.individual_registration === true;
                  }).map((opt) => (
                    <FormControlLabel
                      key={opt.value}
                      value={opt.value}
                      control={<Radio sx={{ color: "#0f172a", "&.Mui-checked": { color: "#0f172a" } }} />}
                      label={
                        <Box>
                          <Typography sx={{ fontFamily: REG_FONT, fontWeight: 600, fontSize: "0.875rem", color: "#0f172a" }}>
                            {opt.label}
                          </Typography>
                          <Typography sx={{ fontFamily: REG_FONT, fontSize: "0.8125rem", color: "#64748b", display: "block", lineHeight: 1.5 }}>
                            {opt.description}
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: "flex-start", mb: 1.25, ml: 0 }}
                    />
                  ))}
                </RadioGroup>
              </>
            )}
            {currentProduct && !teamAllowed && !individualAllowed && (
              <RegBody sx={{ mt: 1, color: "#b91c1c" }}>
                This division does not have registration open for team or individual. Please pick another division.
              </RegBody>
            )}
          </Box>
        )}
        {step === 3 && (
          <Box>
            {region && division && (
              <Box
                sx={{
                  mb: 2.5,
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: "12px",
                  border: "1px solid rgba(15,23,42,0.09)",
                  background: "linear-gradient(165deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.98) 100%)",
                  boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
                }}
              >
                <RegSectionLabel>Your selection</RegSectionLabel>
                <RegTitle sx={{ mb: currentProduct?.name ? 0.5 : 1.5 }}>
                  {currentProduct
                    ? `${currentProduct.city} – ${divisionLabelForProduct(currentProduct.city, currentProduct.division, currentProduct)}`
                    : `${region} – ${division}`}
                </RegTitle>
                {currentProduct && (
                  <>
                    {currentProduct.name && (
                      <RegBody sx={{ color: "#334155", fontWeight: 500, mb: 2, mt: 0 }}>
                        {currentProduct.name}
                      </RegBody>
                    )}
                    <Divider sx={{ borderColor: "rgba(15,23,42,0.08)", mb: 2 }} />
                    <RegMeta label="Location">{currentProduct.location || "TBD"}</RegMeta>
                    <RegMeta label="Schedule">
                      {Array.isArray(currentProduct.timing) && currentProduct.timing.length
                        ? currentProduct.timing.join(", ")
                        : "TBD"}
                    </RegMeta>
                    {currentProduct.description && (
                      <Markdown source={toProductMarkdown(currentProduct.description)} dense sx={{ mb: 2, mt: -0.5, color: "#334155" }} />
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "baseline",
                        gap: 1,
                        pt: 0.5,
                        borderTop: "1px dashed rgba(15,23,42,0.1)",
                      }}
                    >
                      <Typography sx={{ fontFamily: REG_FONT, fontSize: "0.8125rem", fontWeight: 600, color: "#64748b" }}>
                        Fee
                      </Typography>
                      <Typography sx={{ fontFamily: REG_FONT, fontSize: "1.0625rem", fontWeight: 700, color: "#0f172a" }}>
                        {formatCost(currentProduct.cost)}
                      </Typography>
                    </Box>
                    {isSoldOut && (
                      <Typography
                        sx={{
                          fontFamily: REG_FONT,
                          fontSize: "0.875rem",
                          color: "#b91c1c",
                          fontWeight: 600,
                          mt: 1.5,
                        }}
                      >
                        Sold out
                      </Typography>
                    )}
                    <TextField
                      fullWidth
                      size="small"
                      label="Share this registration link"
                      value={
                        typeof window !== "undefined"
                          ? buildRegistrationProductUrl(
                              window.location.origin,
                              currentProduct.city || region,
                              divisionLabelForProduct(currentProduct.city || region, currentProduct.division, currentProduct),
                              currentProduct.id
                            )
                          : ""
                      }
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={copyShareLink} edge="end" aria-label="Copy registration link" size="small">
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mt: 2.5,
                        "& .MuiInputLabel-root": { fontFamily: REG_FONT, fontSize: "0.8125rem" },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          fontFamily: REG_FONT,
                          fontSize: "0.8125rem",
                          backgroundColor: "#fff",
                        },
                      }}
                    />
                    {linkCopied && (
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: REG_FONT, color: "success.main", display: "block", mt: 0.75, fontWeight: 500 }}
                      >
                        Copied to clipboard
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            )}
            {PAYMENTS_ENABLED && !user && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {registrationType === "individual" && individualQuestionsList.length
                  ? "Fill in every required field above, then sign in or create an account to pay."
                  : "Sign in or create an account to proceed to payment."}
              </Alert>
            )}
            {registrationType === "team" ? (
              <>
                <TextField
                  fullWidth
                  label="Team name"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  error={!!errors.teamName}
                  helperText={errors.teamName}
                  sx={{ mb: 2 }}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Captain name"
                  value={captainName}
                  onChange={(e) => setCaptainName(e.target.value)}
                  error={!!errors.captainName}
                  helperText={errors.captainName}
                  sx={{ mb: 2 }}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Captain phone"
                  value={captainPhone}
                  onChange={(e) => setCaptainPhone(e.target.value)}
                  error={!!errors.captainPhone}
                  helperText={errors.captainPhone}
                  placeholder="e.g. 555-123-4567"
                  sx={{ mb: 2 }}
                  size="small"
                />
                {emailConsentBlock}
                {!isCaptainOnlyRegistration && (
                  <>
                <RegBody sx={{ color: "#64748b", mb: 2, fontSize: "0.8125rem" }}>
                  Roster — each email and jersey number must be unique. Instagram is optional (@handle without @).
                </RegBody>
                {players.map((p, i) => (
                  <Paper
                    key={i}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: "12px",
                      borderColor: "rgba(15,23,42,0.1)",
                      backgroundColor: "rgba(248,250,252,0.9)",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                      <Typography
                        variant="overline"
                        sx={{ fontFamily: REG_FONT, color: "#64748b", letterSpacing: "0.08em", fontWeight: 600 }}
                      >
                        Player {i + 1}
                      </Typography>
                      <IconButton size="small" onClick={() => removePlayer(i)} color="error" aria-label="Remove player">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Full name"
                          value={p.name}
                          onChange={(e) => updatePlayer(i, "name", e.target.value)}
                          error={!!errors[`player_name_${i}`]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Email"
                          type="email"
                          autoComplete="email"
                          value={p.email}
                          onChange={(e) => updatePlayer(i, "email", e.target.value)}
                          error={!!errors[`player_email_${i}`]}
                          helperText={errors[`player_email_${i}`]}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Jersey #"
                          type="number"
                          inputProps={{ min: 0, max: 99 }}
                          value={p.jersey_number}
                          onChange={(e) => updatePlayer(i, "jersey_number", e.target.value)}
                          error={!!errors[`player_jersey_${i}`]}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <FormControl fullWidth size="small" error={!!errors[`player_size_${i}`]}>
                          <InputLabel>Jersey size</InputLabel>
                          <Select
                            value={p.jersey_size || ""}
                            label="Jersey size"
                            onChange={(e) => updatePlayer(i, "jersey_size", e.target.value)}
                          >
                            {JERSEY_SIZES.map((s) => (
                              <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Instagram (optional)"
                          placeholder="username"
                          value={p.instagram || ""}
                          onChange={(e) => updatePlayer(i, "instagram", e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={addPlayer}
                  sx={{ fontFamily: REG_FONT, color: "#0f172a", mb: 2, fontWeight: 600, textTransform: "none", borderRadius: "10px" }}
                >
                  Add player
                </Button>
                {errors.players && (
                  <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>
                    {errors.players}
                  </Typography>
                )}
                  </>
                )}
              </>
            ) : individualQuestionsList.length ? (
              <>
                <RegSectionLabel>Registration details</RegSectionLabel>
                <RegBody sx={{ color: "#475569", mb: 2 }}>
                  Complete each item for this league. You sign in with your account; use the league communication email
                  field if it should differ.
                </RegBody>
                {individualQuestionsList.map((q, i) => {
                  const kind = individualSignupFieldKind(q);
                  const err = errors[`individual_q_${i}`];
                  const fieldSx = { mb: 2, "& .MuiInputLabel-root": { fontFamily: REG_FONT } };
                  if (kind === "volunteer") {
                    return (
                      <FormControl key={`q-${i}`} component="fieldset" fullWidth required error={!!err} sx={{ mb: 2 }}>
                        <FormLabel
                          required
                          component="legend"
                          sx={{ fontFamily: REG_FONT, fontSize: "0.875rem", fontWeight: 600, mb: 0.5 }}
                        >
                          {q}
                        </FormLabel>
                        <RadioGroup
                          row
                          value={individualSignupAnswers[q] || ""}
                          onChange={(e) =>
                            setIndividualSignupAnswers((prev) => ({ ...prev, [q]: e.target.value }))
                          }
                        >
                          <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                          <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                        </RadioGroup>
                        {err ? <FormHelperText>{err}</FormHelperText> : null}
                      </FormControl>
                    );
                  }
                  if (kind === "jersey_size") {
                    const jerseyLabelId = `individual-jersey-label-${i}`;
                    const jerseyVal = individualSignupAnswers[q] || "";
                    return (
                      <FormControl key={`q-${i}`} fullWidth required size="small" error={!!err} sx={fieldSx}>
                        <InputLabel id={jerseyLabelId} shrink>
                          {q}
                        </InputLabel>
                        <Select
                          labelId={jerseyLabelId}
                          label={q}
                          notched
                          value={jerseyVal}
                          displayEmpty
                          renderValue={(selected) =>
                            selected ? (
                              selected
                            ) : (
                              <Box component="span" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                                Select a size
                              </Box>
                            )
                          }
                          onChange={(e) =>
                            setIndividualSignupAnswers((prev) => ({ ...prev, [q]: e.target.value }))
                          }
                        >
                          <MenuItem value="" disabled>
                            Select a size
                          </MenuItem>
                          {JERSEY_SIZES.map((s) => (
                            <MenuItem key={s} value={s}>
                              {s}
                            </MenuItem>
                          ))}
                        </Select>
                        {err ? <FormHelperText>{err}</FormHelperText> : null}
                      </FormControl>
                    );
                  }
                  return (
                    <TextField
                      key={`q-${i}`}
                      required
                      fullWidth
                      size="small"
                      label={q}
                      type={kind === "dob" ? "date" : kind === "email" ? "email" : "text"}
                      multiline={kind === "multiline"}
                      minRows={kind === "multiline" ? 3 : undefined}
                      value={individualSignupAnswers[q] || ""}
                      onChange={(e) =>
                        setIndividualSignupAnswers((prev) => ({ ...prev, [q]: e.target.value }))
                      }
                      error={!!err}
                      helperText={err || undefined}
                      InputLabelProps={
                        kind === "dob" || kind === "multiline" || kind === "email" || individualSignupAnswers[q]
                          ? { shrink: true }
                          : undefined
                      }
                      sx={fieldSx}
                    />
                  );
                })}
                {emailConsentBlock}
                <RegBody sx={{ color: "#475569" }}>
                  You’re registering as an individual for {region}, {division}.
                  {PAYMENTS_ENABLED ? " Click Next to proceed to payment." : " Click Submit to complete registration."}
                </RegBody>
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Your name"
                  value={captainName}
                  onChange={(e) => setCaptainName(e.target.value)}
                  error={!!errors.captainName}
                  helperText={errors.captainName}
                  sx={{ mb: 2 }}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Phone number"
                  value={captainPhone}
                  onChange={(e) => setCaptainPhone(e.target.value)}
                  error={!!errors.captainPhone}
                  helperText={errors.captainPhone}
                  placeholder="e.g. 555-123-4567"
                  sx={{ mb: 2 }}
                  size="small"
                />
                {emailConsentBlock}
                <RegBody sx={{ color: "#475569" }}>
                  You’re registering as an individual for {region}, {division}.
                  {PAYMENTS_ENABLED ? " Click Next to proceed to payment." : " Click Submit to complete registration."}
                </RegBody>
              </>
            )}
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            mt: 3,
            gap: 1.5,
          }}
        >
          <Button
            onClick={handleBack}
            disabled={step === 0}
            sx={{ fontFamily: REG_FONT, color: "#64748b", textTransform: "none", fontWeight: 600 }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={checkoutLoading || submitLoading || isSoldOut}
            fullWidth={isNarrow}
            sx={{
              fontFamily: REG_FONT,
              backgroundColor: "#0f172a",
              color: "#fff",
              fontWeight: 600,
              textTransform: "none",
              letterSpacing: "0.02em",
              borderRadius: "10px",
              py: 1.35,
              boxShadow: "0 1px 2px rgba(15,23,42,0.12)",
              "&:hover": { backgroundColor: "#1e293b", boxShadow: "0 4px 12px rgba(15,23,42,0.2)" },
            }}
          >
            {submitLoading
              ? "Submitting…"
              : checkoutLoading
                ? "Redirecting…"
                : step === 3
                  ? !user
                    ? PAYMENTS_ENABLED
                      ? "Sign up to proceed to payment"
                      : "Sign in to submit"
                    : PAYMENTS_ENABLED
                      ? "Proceed to payment"
                      : "Submit"
                  : "Next"}
          </Button>
        </Box>
      </Paper>
      </Box>
    </Box>
  );
};

export default RegisterFlow;
